import { apiFetch } from "@/api/client";

/**
 * Client Portal API — tenant-scoped user administration.
 *
 * **No function here takes an `organization_id`.** The tenant is derived from
 * the caller's JWT claim server-side; sending it would be both redundant and,
 * if it disagreed with the token, a 403 from `TenantScopeMiddleware`. Keeping
 * the org out of the client's hands means there is no code path where a
 * ClientAdmin could ask about someone else's tenant.
 *
 * The user-administration endpoints proxy IAM's delegated-admin API (live since
 * 2026-07-14). **IAM is the enforcement point, not this client and not the BFF**:
 * OU containment, the closed grantable allow-list, last-ClientAdmin protection
 * and the no-unilateral-PHI-grant rule are all its invariants. So the UI's job
 * is to render IAM's answers faithfully — including its refusals — not to
 * pre-empt them with rules of its own.
 */

/** One of a user's group memberships (IAM `?expand=groups`). */
export interface GroupMembership {
  /** The GUID — what grant/revoke key on, not the name. */
  group_id: string;
  group_name: string | null;
  starts_at: string | null;
  /** In the past = lapsed (IAM's resolver already ignores it); null = standing. */
  expires_at: string | null;
}

export interface PortalUser {
  /** IAM's own name for the subject. */
  id: string;
  username: string | null;
  email: string | null;
  /** IAM's `firstName` / `lastName`, absent from older BFF builds. */
  first_name?: string | null;
  last_name?: string | null;
  external_id: string | null;
  /** Our `enabled` is IAM's `isActive`. */
  enabled: boolean;
  /**
   * Memberships. **`null` when the list wasn't `?expand=groups`-ed; `[]` when it
   * was and the user holds none.** The table must tell those apart — "not asked"
   * is not "has no roles", and an admin who reads a confident blank as "none"
   * will grant a role someone already holds.
   */
  groups: GroupMembership[] | null;
}

export interface PortalUserList {
  organization_id: string;
  users: PortalUser[];
}

export interface GrantableGroup {
  /** The GUID — grant/revoke key on this, not the name. */
  group_id: string;
  group_name: string | null;
  description: string | null;
  /**
   * Whether this group carries PHI access — **IAM's flag, not our name-sniff**
   * (Aghiad 2026-07-15: `isPrivileged`). A direct grant of one is rejected
   * server-side; the UI renders it as requestable and routes to the
   * access-request flow. The flag is a UI hint; IAM is the guard.
   */
  is_privileged: boolean;
}

export interface AccessRequest {
  id: string;
  target_user_id: string | null;
  group_id: string | null;
  /** Readable name — the approver reads this, not the GUID. */
  group_name: string | null;
  requested_by: string | null;
  justification: string | null;
  requested_duration_days: number | null;
  /** "Pending" | "Approved" | "Denied". */
  status: string | null;
  /** When an approved membership auto-lapses. */
  grant_expires_at: string | null;
}

/** The caller's effective permissions, resolved from IAM (not the token). */
export interface MyPermissions {
  subject: string;
  permissions: string[];
}

/**
 * `GET /api/v1/portal/me/permissions` — the caller's effective permission keys.
 *
 * The SPA has no other honest source: IAM does not put permission VALUES in the
 * access token, so decoding the JWT yields an empty list for every user. This
 * resolves them server-side.
 */
export function getMyPermissions(): Promise<MyPermissions> {
  return apiFetch<MyPermissions>("/api/v1/portal/me/permissions");
}

/** `GET /api/v1/portal/users` — users in the caller's own organization. */
export function listPortalUsers(): Promise<PortalUserList> {
  return apiFetch<PortalUserList>("/api/v1/portal/users");
}

/** `GET /api/v1/portal/groups` — the roles this admin may grant. */
export function listGrantableGroups(): Promise<GrantableGroup[]> {
  return apiFetch<GrantableGroup[]>("/api/v1/portal/groups");
}

/**
 * The roles a lab admin may pick when inviting (docs#239 item 1). The BFF maps them
 * onto IAM: `Clinician` is IAM's own `role: "clinician"`, which IAM attaches to the
 * lab's Clinician group. IAM's create knows no other staff role, so `ContentEditor`
 * and `ClientAdmin` are a create with no role followed by a grant of the lab's own
 * group for it. IAM still owns the allow-list and refuses what this admin may not give.
 *
 * This is a STAFF portal, so the picker is the staff roles only:
 * - `Patient` is deliberately absent. The portal's create surface calls IAM
 *   `POST /api/users` and nothing else, whereas a patient needs a FHIR `Patient`
 *   anchor too (the cerbo `add-user` path creates BOTH). Inviting a "Patient" here
 *   would mint a dead account no report can ever resolve.
 * - `Admin` (global) and `ApiConsumer` (service account) are not client-grantable.
 * - `Reviewer` carries patient data, so it is requested after the invite, never given.
 */
export const INVITE_ROLES = ["Clinician", "ContentEditor", "ClientAdmin"] as const;
export type InviteRole = (typeof INVITE_ROLES)[number];

export interface CreateUserInput {
  username: string;
  email: string;
  role: InviteRole;
  firstName?: string;
  lastName?: string;
}

/**
 * What an invite returns. `role_granted` is false when the user was created and
 * emailed but the lab group for a ContentEditor or ClientAdmin pick could not be
 * added. `detail` then says what to do, and the page shows it.
 */
export interface CreateUserResult {
  status: string;
  sub: string | null;
  role_granted: boolean;
  detail?: string;
}

/**
 * `POST /api/v1/portal/users` — invite a new user into the caller's own org.
 *
 * No `organization_id`: IAM binds the user to the caller's tenant from the token.
 * Behind `SR_PORTAL_INVITE_ENABLED`, a 503 while that is off. A duplicate
 * username/email comes back 409, a bad field 400, both distinct from the 503, so the
 * form can show the fixable cause inline.
 */
export function createUser(input: CreateUserInput): Promise<CreateUserResult> {
  return apiFetch("/api/v1/portal/users", {
    method: "POST",
    body: JSON.stringify({
      username: input.username,
      email: input.email,
      role: input.role,
      ...(input.firstName ? { first_name: input.firstName } : {}),
      ...(input.lastName ? { last_name: input.lastName } : {}),
    }),
  });
}

/** `POST /api/v1/portal/users/{sub}/groups` */
export function grantGroup(sub: string, group: string): Promise<unknown> {
  return apiFetch(`/api/v1/portal/users/${encodeURIComponent(sub)}/groups`, {
    method: "POST",
    body: JSON.stringify({ group }),
  });
}

/** `DELETE /api/v1/portal/users/{sub}/groups/{group}` */
export function revokeGroup(sub: string, group: string): Promise<unknown> {
  return apiFetch(
    `/api/v1/portal/users/${encodeURIComponent(sub)}/groups/${encodeURIComponent(group)}`,
    { method: "DELETE" },
  );
}

/**
 * `POST /api/v1/portal/users/{sub}/{disable|enable}` — idempotent, per IAM.
 *
 * There is no delete. IAM's is a soft delete, disable is the reversible thing an
 * admin actually wants when someone leaves, and putting both on one screen
 * invites the irreversible one to be misclicked.
 */
export function setUserEnabled(sub: string, enabled: boolean): Promise<unknown> {
  const action = enabled ? "enable" : "disable";
  return apiFetch(`/api/v1/portal/users/${encodeURIComponent(sub)}/${action}`, { method: "POST" });
}

// ---------------------------------------------------------------------------
// PHI-grant approval workflow — the ONLY route to PHI access (§4.3).
// ---------------------------------------------------------------------------

/** `GET /api/v1/portal/access-requests` — the approval queue for this OU. */
export function listAccessRequests(): Promise<AccessRequest[]> {
  return apiFetch<AccessRequest[]>("/api/v1/portal/access-requests");
}

/**
 * `POST /api/v1/portal/access-requests` — ask for a PHI-carrying group.
 *
 * Approval needs a *second* person (IAM enforces approver ≠ requester) and
 * grants a time-boxed membership that lapses on its own. The justification is
 * mandatory: it is what the approver reads.
 */
export function requestGroupAccess(input: {
  targetUserId: string;
  groupId: string;
  justification: string;
  requestedDurationDays?: number;
}): Promise<unknown> {
  return apiFetch("/api/v1/portal/access-requests", {
    method: "POST",
    body: JSON.stringify({
      target_user_id: input.targetUserId,
      group_id: input.groupId,
      justification: input.justification,
      ...(input.requestedDurationDays === undefined
        ? {}
        : { requested_duration_days: input.requestedDurationDays }),
    }),
  });
}

/** `POST /api/v1/portal/access-requests/{id}/{approve|deny}` */
export function decideAccessRequest(id: string, decision: "approve" | "deny"): Promise<unknown> {
  return apiFetch(`/api/v1/portal/access-requests/${encodeURIComponent(id)}/${decision}`, {
    method: "POST",
  });
}

// ---------------------------------------------------------------------------
// Org-scoped audit viewer (X1 / ADR-0051 §Operational gate).
// ---------------------------------------------------------------------------

export interface AuditEvent {
  event_id: string;
  event_type: string;
  occurred_at: string | null;
  received_at: string | null;
  service: string;
  severity: string;
  is_phi: boolean;
  organization_id: string | null;
  patient_id: string | null;
  payload: Record<string, unknown>;
}

export interface AuditEventPage {
  /** The tenant the page is scoped to; `null` on the admin cross-org (fleet) view. */
  organization_id: string | null;
  events: AuditEvent[];
  count: number;
  /**
   * Whether this caller may see PHI-bearing rows — i.e. the critical clinical
   * triggers, which are ALL of them. Resolved from IAM server-side, because the
   * browser cannot know: IAM does not put permission values in the access token.
   *
   * `false` means "you are not being shown the alerts", which is a different
   * sentence from "there are no alerts". Only one of them means no patient needs
   * attention, and an empty table cannot tell them apart on its own.
   */
  phi_visible: boolean;
}

export interface AuditQuery {
  /** Prefix match: `notification.trigger.` returns the whole A.1 family. */
  eventType?: string | undefined;
  severity?: string | undefined;
  since?: string | undefined;
  limit?: number | undefined;
}

/**
 * `GET /api/v1/portal/audit/events` — the caller's own organization's audit log.
 *
 * Note the absence of an `org` argument, as everywhere else in this module. The
 * BFF sends the claim's organization downstream, and `TenantScopeMiddleware`
 * rejects a query string that disagrees with the token.
 */
export function listAuditEvents(query: AuditQuery = {}): Promise<AuditEventPage> {
  const params = new URLSearchParams();
  if (query.eventType) params.set("event_type", query.eventType);
  if (query.severity) params.set("severity", query.severity);
  if (query.since) params.set("since", query.since);
  params.set("limit", String(query.limit ?? 100));
  return apiFetch<AuditEventPage>(`/api/v1/portal/audit/events?${params.toString()}`);
}

/** Admin (staff) cross-region audit query — the full filter set the tenant view omits. */
export interface AdminAuditQuery {
  eventType?: string | undefined;
  severity?: string | undefined;
  since?: string | undefined;
  service?: string | undefined;
  patientId?: string | undefined;
  organizationId?: string | undefined;
  limit?: number | undefined;
}

/**
 * `GET /api/v1/admin/audit/events` — staff cross-org / cross-region audit search.
 *
 * Unlike {@link listAuditEvents}, this carries `organizationId` / `service` /
 * `patientId` filters and is NOT pinned to a single tenant. Enforcement lives
 * downstream: the audit service scopes a tenant token to its own org (staff, with
 * no org claim, get the fleet view) and gates a `patientId` search on the
 * PHI-audit permission. `organization_id` on the returned page is `null` for the
 * fleet view.
 */
export function listAdminAuditEvents(query: AdminAuditQuery = {}): Promise<AuditEventPage> {
  const params = new URLSearchParams();
  if (query.eventType) params.set("event_type", query.eventType);
  if (query.severity) params.set("severity", query.severity);
  if (query.since) params.set("since", query.since);
  if (query.service) params.set("service", query.service);
  if (query.patientId) params.set("patient_id", query.patientId);
  if (query.organizationId) params.set("organization_id", query.organizationId);
  params.set("limit", String(query.limit ?? 200));
  return apiFetch<AuditEventPage>(`/api/v1/admin/audit/events?${params.toString()}`);
}

/** One service's deployed feature flags; `reachable=false` → the BFF could not fetch them. */
export interface ServiceFlags {
  service: string;
  reachable: boolean;
  flags: Record<string, boolean>;
}

export interface FlagsPanel {
  services: ServiceFlags[];
}

/**
 * `GET /api/v1/admin/flags` — the staff deployed-feature-flag panel.
 *
 * Aggregates each service's boolean `SR_*_ENABLED` flags so staff can see what is
 * on per service without a redeploy or log-diving. Flags carry no PHI and no secret
 * values. Fail-soft: an unreachable service degrades to `reachable=false` with empty
 * flags rather than failing the whole panel.
 */
export function getAdminFlags(): Promise<FlagsPanel> {
  return apiFetch<FlagsPanel>("/api/v1/admin/flags");
}

// ---------------------------------------------------------------------------
// LIS messages: every status, held as one filter (2026-09-01, widened docs#182).
// ---------------------------------------------------------------------------

/**
 * Whether a Smart Report was asked for on a message, and what became of it (docs#182).
 * A CLOSED set on the server (`smart_reports_models.ReportOutcome`). `null` means no
 * outcome was recorded for that message, which is not the same as "no report".
 */
export type ReportOutcome = "requested" | "not_requested" | "generated" | "not_generated";

/**
 * Why no report was asked for, or none was produced. CLOSED set on the server
 * (`ReportOutcomeReason`). `not_eligible` deliberately covers two refusals so it does not
 * say which patients hold a Medicus account (docs#182 note 9938).
 */
export type ReportOutcomeReason =
  | "generation_off"
  | "not_eligible"
  | "not_final"
  | "not_processed"
  | "request_failed"
  | "generation_failed"
  | "fhir_read_refused";

/** One message-status projection. PHI-free: identifiers and closed-set words only. */
export interface LisMessage {
  /** The MSH-10 correlation id the client sent — what they look it up by in their LIS. */
  message_id: string;
  organization_id: string;
  /** "received" | "processed" | "failed" | "held-for-review". */
  status: string;
  /** ADR-0017 UPPER_SNAKE outcome code, e.g. INGESTION_PATIENT_MISMATCH. */
  code: string | null;
  /** Report Store id, set only when `report_outcome` is `generated`. */
  report_id: string | null;
  report_outcome: ReportOutcome | null;
  report_outcome_reason: ReportOutcomeReason | null;
  created_at: string;
  updated_at: string;
  schema_version: number;
}

export interface LisMessagePage {
  organization_id: string;
  items: LisMessage[];
  /** Matches BEFORE paging, so a 50-row page can still say how many there are. */
  total: number;
  limit: number;
  offset: number;
}

export interface LisMessageQuery {
  /** Omit or leave empty for EVERY status (the server default since docs#182). */
  statuses?: string[] | undefined;
  since?: string | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}

/**
 * `GET /api/v1/portal/lis/messages`: this organization's lab messages, every status.
 *
 * No `organization_id` argument, as everywhere else in this module: the BFF takes the
 * tenant from the caller's claim, and `TenantScopeMiddleware` rejects a query string
 * that disagrees with the token.
 *
 * Needs onboarding-api's IAM client to hold `ingestion:read` (docs#182, asked of IAM).
 */
export function listLisMessages(query: LisMessageQuery = {}): Promise<LisMessagePage> {
  const params = new URLSearchParams();
  for (const status of query.statuses ?? []) params.append("status", status);
  if (query.since) params.set("since", query.since);
  params.set("limit", String(query.limit ?? 50));
  params.set("offset", String(query.offset ?? 0));
  return apiFetch<LisMessagePage>(`/api/v1/portal/lis/messages?${params.toString()}`);
}

// ---------------------------------------------------------------------------
// Lab reports: the portal Reports page and its review view (Gate B M1 S7).
// ---------------------------------------------------------------------------

/**
 * Where a report stands between generation and the patient. A CLOSED set on the server
 * (`smart_reports_models.ReportReviewState`). `null` means the report is outside the review
 * lifecycle (dispatched on completion, as every report was before Gate B).
 */
export type ReportReviewState = "pending_review" | "approved" | "released" | "withdrawn";

/**
 * Whose report it is (docs#15 note 16595), read by the orchestrator from the report's FHIR
 * Patient with the release path's own rules: the first official name, a full date of birth
 * only (a year alone is null), `male`/`female`/`other` only, and the lab's patient number
 * only under that lab's own system. PHI. Any field may be null.
 */
export interface PortalPatientIdentity {
  display_name: string | null;
  birth_date: string | null;
  sex: string | null;
  lab_patient_number: string | null;
}

/** One list row: identity and review state, no report body (so no audit event). */
export interface PortalReportSummary {
  report_id: string;
  trigger_ref: string;
  generated_at: string;
  report_status: string;
  review_state: ReportReviewState | null;
  approved_by: string | null;
  approved_at: string | null;
  released_at: string | null;
  withdrawn_by: string | null;
  withdrawn_at: string | null;
  supersedes_report_id: string | null;
  /**
   * The highest severity among the report's results ("Critical", "Recommended", "None"), or
   * null when no result carries one. Added by onboarding-api so the list can put reports
   * with a Critical result first. Absent (undefined) from a BFF that predates it, and the
   * page then orders newest first.
   */
  highest_severity?: string | null;
  /** Null when the Patient could not be read. Absent from a BFF that predates it. */
  patient?: PortalPatientIdentity | null;
  /** The lab's own order number, null when none was recorded. */
  lab_order_number?: string | null;
  /**
   * The id the lab's own system was told this report by (`rpt_...`), on its report events
   * and message status, so a doctor can match the report to the lab's message (docs#239
   * item 26). Null when lab report events are off, the lab is then told `report_id` itself.
   * Absent from a BFF that predates it.
   */
  lab_report_id?: string | null;
}

export interface PortalReportPage {
  /** The caller's own lab, from their token. */
  organization_id: string;
  /** The lab's name from its config, null when it sets none (then show the id). */
  organization_name?: string | null;
  /** The lab's IANA timezone from its config, null when unset. */
  organization_timezone?: string | null;
  reports: PortalReportSummary[];
  /** Matches before paging. */
  total: number;
  limit: number;
  offset: number;
}

/**
 * One stored report as the BFF returns it, onboarding-api's `PortalReport` projection of the
 * orchestrator's `StoredReport`. It leaves out the patient's identifiers (`patient_id`,
 * `patient_sub`) and the delivery and store fields, so none of them reach the browser.
 *
 * `report_payload` is held opaquely by the store, so it is typed as unknown here and read
 * defensively by the review view: a payload shape this page does not expect must render
 * what it can, never crash the page.
 */
export interface PortalReport {
  report_id: string;
  organization_id: string;
  report_status: string;
  trigger_ref: string;
  generated_at: string;
  report_payload: Record<string, unknown>;
  review_state?: ReportReviewState | null;
  approved_by?: string | null;
  approved_at?: string | null;
  released_at?: string | null;
  withdrawn_by?: string | null;
  withdrawn_at?: string | null;
  supersedes_report_id?: string | null;
  /**
   * The lab's content span the payload's insights were filtered to (docs#199 audit item 11),
   * resolved by the orchestrator the way its release path resolves it. Absent from a BFF
   * that predates it, and the payload is then unfiltered.
   */
  content_span?: "XS" | "S" | "M" | "L";
  content_span_source?: "config" | "default" | "flag_off" | "fail_closed";
  /** Insights the report carries that the span keeps from the patient. */
  insights_outside_span?: number;
  /**
   * Those insights themselves, for the reviewer to read (Baher, docs#15 note 16595 item 9).
   * Never part of `report_payload`, which stays the report as the patient is sent it.
   */
  insights_held_back?: unknown[];
  patient?: PortalPatientIdentity | null;
  lab_order_number?: string | null;
  /**
   * The id the lab's own system was told this report by (`rpt_...`), on its report events
   * and message status, so a doctor can match the report to the lab's message (docs#239
   * item 26). Null when lab report events are off, the lab is then told `report_id` itself.
   * Absent from a BFF that predates it.
   */
  lab_report_id?: string | null;
  organization_name?: string | null;
  organization_timezone?: string | null;
}

export interface PortalReportQuery {
  /** Omit or leave empty for every report. */
  reviewStates?: ReportReviewState[] | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}

/**
 * `GET /api/v1/portal/reports`: this lab's stored reports, Critical first, then newest.
 *
 * No `organization_id` argument, as everywhere else in this module: the BFF and the
 * orchestrator both take the lab from the signed-in user's own token. Needs
 * `smart-reports-insights:requests:clinical:read` and exists only with
 * SR_PORTAL_REPORTS_ENABLED on.
 */
export function listPortalReports(query: PortalReportQuery = {}): Promise<PortalReportPage> {
  const params = new URLSearchParams();
  for (const state of query.reviewStates ?? []) params.append("review_state", state);
  params.set("limit", String(query.limit ?? 50));
  params.set("offset", String(query.offset ?? 0));
  return apiFetch<PortalReportPage>(`/api/v1/portal/reports?${params.toString()}`);
}

/**
 * `GET /api/v1/portal/reports/{report_id}`: one report of this lab. Every successful call
 * is recorded server-side as a `report.viewed` audit event. Another lab's report is a 404,
 * the same answer as one that does not exist.
 */
export function getPortalReport(reportId: string): Promise<PortalReport> {
  return apiFetch<PortalReport>(`/api/v1/portal/reports/${encodeURIComponent(reportId)}`);
}

/**
 * `POST /api/v1/portal/reports/{report_id}/approve`: "Approve and send" (Gate B M2 S11).
 * The report comes back `released`. 409 when it is no longer waiting, 404 for another lab's
 * report or none. Recorded server-side as `report.approved`. Exists only with
 * SR_PORTAL_REPORT_APPROVAL_ENABLED on.
 */
export function approvePortalReport(reportId: string): Promise<PortalReport> {
  return apiFetch<PortalReport>(`/api/v1/portal/reports/${encodeURIComponent(reportId)}/approve`, {
    method: "POST",
  });
}

/**
 * `POST /api/v1/portal/reports/{report_id}/withdraw`: the report is never sent. Terminal.
 * 409 for a released or withdrawn report. Recorded server-side as `report.withdrawn`.
 */
export function withdrawPortalReport(reportId: string): Promise<PortalReport> {
  return apiFetch<PortalReport>(`/api/v1/portal/reports/${encodeURIComponent(reportId)}/withdraw`, {
    method: "POST",
  });
}
